/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Inject, Input, OnInit, signal} from '@angular/core';
import * as _ from 'lodash';

import template from './adunit-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {normalizeTargetingSearchPage} from '../normalize-targeting-search-page';
import {TargetingTemplateHook} from './targeting-template.component';
import {findCountryName} from '../../common/directives/country-select/countries';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {AdUnitApiService} from '../../ad-unit/ad-unit-api.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {SearchApiService} from '../../common/controller/search-api.service';

import {TARGETING_IMPORTS} from '../targeting-imports';
import {TargetingTemplateComponent} from './targeting-template.component';

@Component({
  imports: [...TARGETING_IMPORTS, TargetingTemplateComponent],
  selector: 'adn-ad-unit-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdUnitTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.adUnit;
  @Input() underModel: any;
  @Input() modelLineItem: any;
  @Input() runningModel: any;
  @Input() mandatoryTargets: any;
  @Input() lineItemType: string;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  isPureMarketplace = false;
  private readonly listState = signal<any>({searchResults: []}, {equal: () => false});
  get list(): any { return this.listState(); }
  set list(value: any) { this.listState.set(value); }
  filter: any = {};
  limited = false;
  limitedType = 'earnings account';
  hasUrls = false;
  hasCountries = false;
  hasCountriesTable = false;
  countryBuckets: any[] = [];
  siteUrlBuckets: any[] = [];
  private tabLoaded = false;
  private searchRequestId = 0;
  readonly searchResolved = signal(true);
  readonly filterSearchResolved = signal(true);

  searchMeta: any = {
    pageSize: 50,
    currentPage: 1,
    rangeType: 'LTE',
    term: '',
    amount: null as number | null,
    resolvedError: '',
    filterResolvedError: '',
    filterResolved: true,
    resolved: true,
    changePage: () => this.search(false, true)
  };

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      const model = this.model || {};
      const summary = (model.negated === true ? 'NOT: ' : '') + _.map(model.adUnits, (item: any) => item.name).join(', ');
      return {
        vm: _.cloneDeep(model),
        model: _.cloneDeep(model),
        constant: ADN_TARGETING_TYPES.adUnit,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    private readonly adUnitApi: AdUnitApiService,
    private readonly searchApi: SearchApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
  }

  ngOnInit(): void {
    this.isPureMarketplace = this.LocalNetworkProfile.isPureMarketplacePlatform();
    this.ensureModel();
    this.initCountryFilter();

    if (!this.tabController) {
      return;
    }

    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.adUnit.widget, () => this.onTabSelect(), copyTemplate);
  }

  selectAll(): void {
    _.forEach(_.get(this.list, ['searchResults'], []), (au: any) => {
      au.selected = true;
      this.model.adUnits.push(au);
    });
    this.model.adUnits = _.uniqBy(this.model.adUnits, 'id');
  }

  removeAll(): void {
    this.model.adUnits = [];
    if (this.list && this.list.searchResults) {
      _.forEach(this.list.searchResults, (au: any) => {
        au.selected = false;
      });
    }
  }

  select(adUnit: any): void {
    adUnit.selected = true;
    // Keep the same object reference as the search hit so
    // status/description/dimensions from search show in the targeted table.
    this.model.adUnits.push(adUnit);
  }

  deselect(adUnit: any): void {
    _.remove(this.model.adUnits, ['id', adUnit.id]);
    if (this.list && this.list.searchResults) {
      (_.find(this.list.searchResults, ['id', adUnit.id]) || {}).selected = false;
    }
  }

  search(newSearch?: boolean, filterSearch?: boolean): void {
    const metaResolvedParam = filterSearch ? 'filterResolved' : 'resolved';
    const metaResolvedErrorParam = metaResolvedParam + 'Error';
    this.setSearchResolved(metaResolvedParam, false);
    this.searchMeta[metaResolvedErrorParam] = '';
    const requestId = ++this.searchRequestId;
    this.list = this.list;

    if (!filterSearch) {
      this.filter = {
        siteUrl: null,
        country: _.get(this.filter, ['country']) || null
      };
    }

    if (newSearch) {
      this.searchMeta.currentPage = 1;
    }

    const amount = this.searchMeta.amount === '' || this.searchMeta.amount === null || this.searchMeta.amount === undefined
      ? null
      : Number(this.searchMeta.amount);

    const queryParams: any = {
      q: this.searchMeta.term || '',
      types: 'AdUnit',
      objectState: 'ACTIVE',
      pageSize: this.searchMeta.pageSize,
      page: this.searchMeta.currentPage - 1
    };
    if (_.get(this.modelLineItem, ['axProductIsHighImpact'])) {
      queryParams.isFullPage = true;
    }
    if (this.filter.country) {
      queryParams.country = this.filter.country;
    }
    if (_.isFinite(amount)) {
      queryParams.rangeField = 'floorPrice.amount';
      queryParams.rangeType = this.searchMeta.rangeType;
      queryParams.rangeValue = amount;
    }
    if (this.filter.siteUrl) {
      queryParams.siteUrl = this.filter.siteUrl;
    }
    if (this.lineItemType === 'POLITICAL') {
      queryParams.politicalAdsBlocked = false;
    }
    const targeting: any = this.runningModel || {};
    if (this.model && targeting.siteTarget && targeting.siteTarget.sites && targeting.siteTarget.negated === false &&
      (!this.model.matchingGroup || targeting.siteTarget.matchingGroup !== this.model.matchingGroup)) {
      queryParams.site = _.map(targeting.siteTarget.sites, (s: any) => s.id || s);
    }
    if (this.model && targeting.publisherTarget && targeting.publisherTarget.earningsAccounts && targeting.publisherTarget.negated === false &&
      (!this.model.matchingGroup || targeting.publisherTarget.matchingGroup !== this.model.matchingGroup)) {
      queryParams.earningsAccount = _.map(targeting.publisherTarget.earningsAccounts, (s: any) => s.id || s);
    }
    const orderId = _.get(this.modelLineItem, ['order', 'id']);
    if (orderId) {
      queryParams.filterBy = 'ORDER_TEAM';
      queryParams.filterByValue = orderId;
    }

    this.searchApi.search(queryParams).subscribe({
      next: (page) => {
        if (requestId !== this.searchRequestId) {
          return;
        }
        const normalizedPage = this.normalizeSearchPage(page);
        const limited = (queryParams.site && queryParams.site.length) > 0 ||
          (queryParams.earningsAccount && queryParams.earningsAccount.length) > 0;
        const limitedType = this.isPureMarketplace ? 'publisher' : 'earnings account';
        let siteUrlBuckets = [];
        let hasUrls = false;
        let countryBuckets = [];
        let hasCountries = false;
        let hasCountriesTable = false;
        let resolvedError = this.searchMeta[metaResolvedErrorParam];

        try {
          siteUrlBuckets = this.normalizeBuckets(_.get(normalizedPage, ['aggregations', 'siteUrl', 'buckets']));
          hasUrls = !!(this.filter.siteUrl || !_.isEmpty(siteUrlBuckets));

          countryBuckets = this.normalizeBuckets(_.get(normalizedPage, ['aggregations', 'country', 'buckets']));
          const isMandatory = (this.mandatoryTargets || []).indexOf(ADN_TARGETING_TYPES.siteCountry.id) > -1;
          hasCountries = this.localNetworkInfo.getSiteCountries().length > 1 && !isMandatory &&
            !!(this.filter.country || !_.isEmpty(countryBuckets));
          hasCountriesTable = this.localNetworkInfo.getSiteCountries().length > 1 &&
            !!(this.filter.country || !_.isEmpty(countryBuckets));
          if (hasCountries) {
            _.forEach(countryBuckets, (country: any) => {
              country.name = findCountryName(country.name);
            });
          }
        } catch (err: any) {
          if (!resolvedError) {
            resolvedError = err?.message || 'Unable to process ad unit search filters';
          }
        }
        if (requestId !== this.searchRequestId) {
          return;
        }
        this.limited = limited;
        this.limitedType = limitedType;
        this.siteUrlBuckets = siteUrlBuckets;
        this.hasUrls = hasUrls;
        this.countryBuckets = countryBuckets;
        this.hasCountries = hasCountries;
        this.hasCountriesTable = hasCountriesTable;
        if (resolvedError) {
          this.searchMeta[metaResolvedErrorParam] = resolvedError;
        }
        this.setSearchResolved(metaResolvedParam, true);
        // Publish the signal after the related plain state has settled so the
        // zoneless OnPush view observes the completed search in one render.
        this.list = normalizedPage;
        this.updateSelectedSearchResults();
      },
      error: (err: any) => {
        if (requestId !== this.searchRequestId) {
          return;
        }
        this.setSearchResolved(metaResolvedParam, true);
        if (_.get(err, ['data', 'code']) === 'error.api.order.no.team') {
          this.searchMeta[metaResolvedErrorParam] = 'Specify a team on the order to obtain ad unit results here';
        } else {
          const statusText = _.get(err, ['statusText']) || _.get(err, ['message']);
          if (statusText) {
            this.searchMeta[metaResolvedErrorParam] = statusText;
          }
        }
        this.list = _.get(this.list, ['searchResults', 'length']) ? this.list : {searchResults: []};
      }
    });
  }

  changePage(page: number): void {
    if (page < 1) {
      return;
    }
    this.searchMeta.currentPage = page;
    this.search(false, true);
  }

  private onTabSelect(): void {
    if (this.tabLoaded) {
      return;
    }
    this.tabLoaded = true;
    this.search();
    if (!this.model.adUnits.length) {
      return;
    }
    const ids = _.map(this.model.adUnits, 'id');
    const applyHydration = (page: any) => {
      const keyedResults = _.keyBy(page.results || page || [], 'id');
      _.forEach(this.model.adUnits, (sel: any) => {
        const fullAdUnitDetail = keyedResults[sel.id];
        if (!fullAdUnitDetail) {
          return;
        }
        // Assign in place so selected search-row references also update.
        _.assign(sel, _.pick(fullAdUnitDetail, [
          'description', 'minWidth', 'minHeight', 'width', 'height', 'floorPrice',
          'requestedInLastDay', 'dimensions', 'isFullPage', 'fullPageFormats', 'summary',
          'politicalAdsApprovalRequired'
        ]));
        sel.countryExpanded = fullAdUnitDetail.country ? findCountryName(fullAdUnitDetail.country) : '';
      });
      this.list = this.list;
    };
    this.adUnitApi.getMany(ids).subscribe(applyHydration);
  }

  private ensureModel(): void {
    if (!this.model) {
      return;
    }
    this.model.adUnits = this.model.adUnits || [];
    if (_.isNil(this.model.negated)) {
      this.model.negated = false;
    }
  }

  private initCountryFilter(): void {
    const countriesSelected = _.get(this.runningModel, [
      ADN_TARGETING_TYPES.siteCountry.targets,
      ADN_TARGETING_TYPES.siteCountry.apiProp
    ]);
    if (_.isArray(countriesSelected) && countriesSelected.length === 1) {
      this.filter = this.filter || {};
      this.filter.country = countriesSelected[0].toLowerCase();
    }
  }

  private updateSelectedSearchResults(): void {
    const selectedById = _.keyBy(_.get(this.model, ['adUnits'], []), 'id');
    _.forEach(_.get(this.list, ['searchResults'], []), (searchHit: any) => {
      const selected = selectedById[searchHit.id];
      searchHit.selected = !!selected;
      if (!selected) {
        return;
      }
      // Fill status/description/dimensions on targeted items from search hits.
      _.assign(selected, _.pick(searchHit, [
        'description', 'summary', 'requestedInLastDay', 'dimensions',
        'minWidth', 'minHeight', 'width', 'height', 'floorPrice',
        'isFullPage', 'fullPageFormats', 'politicalAdsApprovalRequired',
        'countryExpanded', 'siteUrl'
      ]));
    });
  }

  private normalizeSearchPage(page: any): any {
    // Mutate hits in place, as site targeting does, so field references stay intact.
    return normalizeTargetingSearchPage(page, {
      pageSize: this.searchMeta.pageSize,
      currentPage: this.searchMeta.currentPage,
      mapItem: (item: any) => {
        item.countryExpanded = item.country ? findCountryName(item.country) : '';
        return item;
      }
    });
  }

  private normalizeBuckets(buckets: any): any[] {
    const bucketList = _.isArray(buckets) ? buckets : (_.isObject(buckets) ? _.values(buckets) : []);
    return _.filter(bucketList, (bucket: any) => {
      return bucket && (bucket.filter !== null || bucket.name !== null) &&
        (bucket.filter !== undefined || bucket.name !== undefined);
    });
  }

  private setSearchResolved(param: string, value: boolean): void {
    this.searchMeta[param] = value;
    (param === 'filterResolved' ? this.filterSearchResolved : this.searchResolved).set(value);
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || {adUnits: []};
    Object.keys(this.model).forEach((key) => delete this.model[key]);
    Object.assign(this.model, copied);
    this.ensureModel();
    this.updateSelectedSearchResults();
  }
}
